'use client'

import * as Sentry from '@sentry/nextjs'
import { Bug } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/hooks/use-toast'

type SentryFeedbackTriggerProps = {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  hideTrigger?: boolean
}

export function SentryFeedbackTrigger({ open: openProp, onOpenChange, hideTrigger = false }: SentryFeedbackTriggerProps = {}) {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const [message, setMessage] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const open = openProp ?? internalOpen

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (openProp === undefined) setInternalOpen(next)
      onOpenChange?.(next)
    },
    [openProp, onOpenChange]
  )

  const canSubmit = Boolean(message.trim()) && !submitting

  const onSubmit = async () => {
    const text = message.trim()
    if (!text) return
    setSubmitting(true)
    try {
      // Bring-your-own widget: capture feedback with Sentry user context already set via Sentry.setUser().
      ;(Sentry as any).captureFeedback?.(
        { message: text },
        { includeReplay: true }
      )
      try {
        if (typeof Sentry.metrics?.count === 'function') {
          Sentry.metrics.count('report_bug', 1, {
            attributes: { message_length: String(text.length) },
          })
          void Sentry.flush(2000)
        }
      } catch (metricErr) {
        console.error('[Sentry] Failed to send report_bug metric', metricErr)
      }
      toast({ title: 'Thanks!', description: 'Your bug report was sent.' })
      setOpen(false)
      setMessage('')
    } catch (e) {
      toast({
        title: 'Could not send report',
        description: String((e as any)?.message || 'Unknown error'),
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v)
        if (!v) {
          setSubmitting(false)
          setMessage('')
        }
      }}
    >
      {!hideTrigger ? (
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm" className="flex items-center gap-2" aria-label="Report Bug" title="Report Bug">
            <Bug className="h-4 w-4" />
            Report Bug
          </Button>
        </DialogTrigger>
      ) : null}

      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Report Bug</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            placeholder="Describe the issue..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="min-h-[160px]"
          />
        </div>

        <DialogFooter>
          <Button onClick={onSubmit} disabled={!canSubmit}>
            {submitting ? 'Sending…' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

